import type { RefrigerationUnit, RefrigerationUnitSafetyThreshold } from '../../schemas';
import { newId } from '../../../utils/ids';

import { pick, pickWeighted, randomInt, shuffle, type Rng } from './rng';

const UNIT_COUNT_DISTRIBUTION = [
  { value: 1, weight: 0.75 },
  { value: 2, weight: 0.2 },
  { value: 3, weight: 0.05 },
] as const;

const DETECTION_ACTIONS = [
  'siren',
  'signal',
  'fans',
  'scrubber',
  'power_cutoff',
  'dialer',
] as const;

const ABATEMENT_CONTROL_MODES = ['manuale', 'automatico'] as const;

export function generateRefrigerationUnits(
  siteId: string,
  rng: Rng,
  options?: {
    minUnits?: number;
    plannedPeriod?: string;
    plannedDateChance?: number;
  }
): RefrigerationUnit[] {
  const units = Math.max(pickWeighted(rng, UNIT_COUNT_DISTRIBUTION), options?.minUnits ?? 0);
  const timestamp = new Date().toISOString();
  const plannedPeriod = options?.plannedPeriod;
  const plannedDateChance = options?.plannedDateChance ?? 0.4;

  return Array.from({ length: units }, (_unused, index) => {
    const unitId = `${siteId}-U${index + 1}`;
    const positionLetters = ['A', 'B', 'C', 'D'];
    const components = [
      {
        id: newId(),
        type: 'compressore' as const,
        quantity: randomInt(rng, 1, 2),
        capacityKw: randomInt(rng, 30, 90),
        location: `Locale ${positionLetters[index] ?? 'A'}`,
        notes: rng() > 0.6 ? 'Controllo vibrazioni programmato' : undefined,
      },
      {
        id: newId(),
        type: 'condensatore' as const,
        quantity: 1,
        capacityKw: randomInt(rng, 60, 120),
        location: rng() > 0.5 ? 'Copertura' : 'Cortile',
      },
      {
        id: newId(),
        type: 'evaporatore' as const,
        quantity: randomInt(rng, 1, 2),
        capacityKw: randomInt(rng, 10, 35),
        location: 'Cella frigorifera',
      },
    ];

    const thresholds = Array.from(
      { length: randomInt(rng, 1, 2) },
      (): RefrigerationUnitSafetyThreshold => ({
        id: newId(),
        thresholdType: rng() > 0.7 ? 'esplosivita' : 'tossicita',
        unit: rng() > 0.2 ? 'ppm' : 'percent_lel',
        value: rng() > 0.2 ? randomInt(rng, 40, 120) : randomInt(rng, 1, 4),
        actions: shuffle(rng, DETECTION_ACTIONS).slice(0, randomInt(rng, 2, 3)),
      })
    );

    const ventilationType = rng() > 0.4 ? 'Forzata' : 'Naturale';
    const ventilation =
      rng() > 0.1
        ? {
            type: ventilationType,
            flowRateM3h:
              ventilationType === 'Forzata'
                ? randomInt(rng, 9000, 16000)
                : randomInt(rng, 4000, 7000),
            naturalPresent: ventilationType === 'Naturale',
            apertureHighM2: ventilationType === 'Naturale' ? randomInt(rng, 3, 6) : undefined,
            apertureLowM2: ventilationType === 'Naturale' ? randomInt(rng, 2, 4) : undefined,
            natural:
              ventilationType === 'Naturale'
                ? {
                    highOpeningsAreaM2: randomInt(rng, 3, 6),
                    lowOpeningsAreaM2: randomInt(rng, 2, 4),
                    notes: rng() > 0.6 ? 'Aperture da mantenere sgombre' : undefined,
                  }
                : undefined,
            extractors:
              ventilationType === 'Forzata'
                ? {
                    units: randomInt(rng, 2, 4),
                    airflowM3h: randomInt(rng, 12000, 18000),
                    atex: rng() > 0.5,
                    logic: rng() > 0.5 ? 'PRIMA SOGLIA' : 'SECONDA SOGLIA',
                    mode: rng() > 0.5 ? 'Automatico' : 'Manuale',
                  }
                : undefined,
            extractorsCount: ventilationType === 'Forzata' ? randomInt(rng, 2, 4) : undefined,
            extractorsFlowM3h:
              ventilationType === 'Forzata' ? randomInt(rng, 12000, 18000) : undefined,
            extractorsOperation:
              ventilationType === 'Forzata'
                ? rng() > 0.5
                  ? 'PRIMA SOGLIA'
                  : 'SECONDA SOGLIA'
                : undefined,
            extractorsAtex: ventilationType === 'Forzata' ? rng() > 0.5 : undefined,
          }
        : undefined;

    const scrubber = {
      present: rng() > 0.5,
      notes: rng() > 0.6 ? 'Manutenzione trimestrale registrata' : undefined,
      airFlowM3h: randomInt(rng, 2000, 4000),
      waterFlowM3h: randomInt(rng, 3, 6),
      waterFlowSpec:
        rng() > 0.5
          ? `n.${randomInt(rng, 16, 24)} ugelli da ${randomInt(rng, 6, 10)} l/min`
          : undefined,
      contactTimeSeconds: randomInt(rng, 25, 45),
      waterSupply: rng() > 0.5 ? 'Acquedotto' : 'Vasca tecnica',
      wasteCollection: rng() > 0.5 ? 'Pozzetto dedicato' : 'Linea reflui',
      atex: rng() > 0.5,
    };

    const abatementSystemPresent = rng() > 0.45;
    const abatementSystem = {
      present: abatementSystemPresent,
      controlMode: abatementSystemPresent ? pick(rng, ABATEMENT_CONTROL_MODES) : undefined,
      trim: abatementSystemPresent ? rng() > 0.5 : undefined,
      limitSwitches: abatementSystemPresent ? rng() > 0.5 : undefined,
      type:
        abatementSystemPresent && rng() > 0.5
          ? pick(rng, [
              'Ventilazione su collettore generale',
              'Scrubber centralizzato',
              'Bypass su camino di espulsione',
            ])
          : undefined,
      supply:
        abatementSystemPresent && rng() > 0.5
          ? pick(rng, ['Linea acqua industriale', 'Rete idrica dedicata', 'Serbatoio tecnico'])
          : undefined,
      disposal:
        abatementSystemPresent && rng() > 0.5
          ? pick(rng, ['Canalizzazione reflui', 'Pozzetto raccolta', 'Vasca equalizzazione'])
          : undefined,
      waterFlowM3h: abatementSystemPresent ? randomInt(rng, 3, 9) : undefined,
    };

    const powerBackup = {
      preferredLine: rng() > 0.5,
      generator: rng() > 0.4,
      ups: rng() > 0.4,
      notes: rng() > 0.6 ? 'Verificare autonomia UPS trimestralmente' : undefined,
    };

    const emergencySystems = {
      dialerPresent: rng() > 0.5,
      guardServicePresent: rng() > 0.4,
      generatorPresent: rng() > 0.5,
      // New safety systems fields
      elettroserraturaPresent: rng() > 0.6,
      emergencyLightingPresent: rng() > 0.8,
      emergencyLightingAtex: rng() > 0.9,
      sgancioAutomaticoPresent: rng() > 0.7,
      sogliaSgancioAutomatico: randomInt(rng, 25, 100) + ' ppm',
      pulsanteSgancioPresent: rng() > 0.6,
    };

    const valves = {
      reliefSetPressuresBar: [randomInt(rng, 14, 18), randomInt(rng, 18, 22)],
      reliefPeriodicityYears: 3,
      reliefNotes: rng() > 0.7 ? 'Programmato intervento con officina certificata' : undefined,
      isolationPresent: true,
      isolationDiverter: rng() > 0.6,
      isolationNotes: rng() > 0.7 ? 'Verificare funzionamento deviatori annualmente' : undefined,
    };

    const detectionMaintenance =
      rng() > 0.5
        ? {
            name: rng() > 0.5 ? 'TechGas Service' : 'Guardiania H24',
            notes: rng() > 0.6 ? 'Contratto semestrale in corso' : undefined,
          }
        : undefined;

    const vsRelief = {
      collectorsCount: randomInt(rng, 1, 3),
      sizing: `DN${randomInt(rng, 80, 120)}`,
    };

    const abatementLocation: 'interrata' | 'fuori_terra' =
      rng() > 0.6 ? 'interrata' : 'fuori_terra';
    const abatementSignage: 'segnalata' | 'non_segnalata' =
      rng() > 0.6 ? 'segnalata' : 'non_segnalata';
    const abatementAccessibility: 'agevole' | 'difficoltosa' =
      rng() > 0.6 ? 'agevole' : 'difficoltosa';
    const abatementMaterial = pick(rng, ['ca', 'acciaio_zincato', 'vetroresina', 'polietilene']) as
      | 'ca'
      | 'acciaio_zincato'
      | 'vetroresina'
      | 'polietilene';
    const abatementOrientation: 'orizzontale' | 'verticale' =
      rng() > 0.5 ? 'orizzontale' : 'verticale';
    const instrumentationType = pick(rng, ['elettronico', 'visivo', 'meccanico']) as
      | 'elettronico'
      | 'visivo'
      | 'meccanico';
    const abatementTank = {
      present: true,
      volumeMc: randomInt(rng, 8, 15),
      minWaterMc: randomInt(rng, 3, 6),
      minFreeMc: randomInt(rng, 1, 3),
      location: abatementLocation,
      signage: abatementSignage,
      accessibility: abatementAccessibility,
      material: abatementMaterial,
      dimensions:
        rng() > 0.5
          ? `Ø=${randomInt(rng, 2, 3)}m, Lu=${randomInt(rng, 3, 5)}m`
          : `H=${randomInt(rng, 2, 3)}m, L=${randomInt(rng, 3, 5)}m, Lu=${randomInt(rng, 2, 4)}m`,
      orientation: abatementOrientation,
      levelInstrumentation:
        rng() > 0.4 ? { present: true, type: instrumentationType } : { present: false },
      continuousPhMonitoring: rng() > 0.5,
      emergencySupply: {
        preferredLine: rng() > 0.5,
        generator: rng() > 0.4,
        ups: rng() > 0.4,
      },
    };

    const vvfDocs: 'prevista' | 'non_prevista' = rng() > 0.6 ? 'prevista' : 'non_prevista';
    const vvfDocsLocation =
      vvfDocs === 'prevista' ? `Armadio sicurezza ${positionLetters[index] ?? 'A'}` : undefined;
    const plannedDate =
      plannedPeriod && rng() > plannedDateChance
        ? `${plannedPeriod}-${String(randomInt(rng, 5, 25)).padStart(2, '0')}`
        : undefined;
    const plannedSource = plannedDate ? 'manual' : plannedPeriod ? 'periodic' : undefined;

    return {
      id: newId(),
      refrigerationUnitId: unitId,
      name: `Impianto ${index + 1}`,
      location: {
        kind: 'custom',
        text: `Locale ${positionLetters[index] ?? 'A'}`,
      },
      ammoniaKg: randomInt(rng, 250, 480),
      toxicGasType: {
        kind: 'option',
        optionId: pick(rng, ['ammoniaca-anidra', 'anidride-solforosa', 'cianuri']),
      },
      impiantoType: {
        kind: 'custom',
        text: pick(rng, [
          'Refrigerazione industriale',
          'Logistica del freddo',
          'Condizionamento e climatizzazione',
          'Processi alimentari',
        ]),
      },
      plannedPeriod: plannedPeriod ?? undefined,
      plannedDate,
      plannedSource,
      plannedUpdatedAt: plannedPeriod || plannedDate ? timestamp : undefined,
      technical: {
        components,
        maintenanceContractor:
          rng() > 0.5
            ? {
                name: rng() > 0.5 ? 'FrigoService Nord' : 'Manutenzione Alfa',
                notes: rng() > 0.6 ? 'Intervento programmato mese prossimo' : undefined,
              }
            : undefined,
        safety: {
          detectors: {
            toxicCount: randomInt(rng, 2, 4),
            explosiveCount: rng() > 0.6 ? 1 : 0,
            positions: shuffle(rng, [
              'Ingresso',
              'Evaporatori',
              'Locale quadri',
              'Area compressori',
            ]).slice(0, randomInt(rng, 2, 3)),
            thresholds,
            calibrationNotes: rng() > 0.6 ? 'Taratura eseguita a febbraio' : undefined,
            ceilingCount: randomInt(rng, 1, 3),
            humanHeightCount: rng() > 0.3 ? randomInt(rng, 1, 3) : undefined,
          },
          ventilation,
          scrubber,
          abatementSystem,
          powerBackup,
          emergencySystems,
          valves,
          detectionMaintenance,
          vsRelief,
          abatementTank,
          vvfDocs,
          vvfDocsLocation,
        },
        notes: rng() > 0.6 ? 'Monitorare rumorosità compressori' : undefined,
      },
      createdAt: timestamp,
      updatedAt: timestamp,
    } satisfies RefrigerationUnit;
  });
}
